import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import "./EcommerceFavoritesFromRecentModal.scss";
import { useAppContextNonNull } from "@contexts/AppContext";
import { useFavorites } from "@contexts/FavoritesContext";
import RecentItemsService, { IRecentFavoriteCandidate } from "@services/RecentItemsService";
import IImageSizeEnum from "@models/IImageSizeEnum";
import INotification from "@models/INotification";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import ActivityIndicator from "../../shared/ActivityIndicator/ActivityIndicator";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwS3ImageViewer from "../../shared/OwS3ImageViewer/OwS3ImageViewer";
import OwTitle from "../../shared/OwTitle/OwTitle";
import { OwP } from "../../shared/OwP/OwP";

/** Extra class for uiContext.setModalSetup so the modal fits a phone width (see the .scss). */
export const FAVORITES_FROM_RECENT_MODAL_CONTENT_CLASS = "modalContentClass EcommerceFavoritesFromRecentModal-content";

type Props = {
    closeFn: () => void;
    /** Called after a successful bulk add (the favorites context has already been updated). */
    onAdded?: (addedGuids: string[]) => void;
    /** Shown in the empty state; the modal closes first. */
    onViewFullCatalog?: () => void;
};

/**
 * "Build from recent orders": the customer's items from the last six months with checkboxes,
 * select-all, and a single bulk add to favorites (POST /v1/favorites/bulk).
 */
export const EcommerceFavoritesFromRecentModal: React.FC<Props> = (props) => {
    const { userModel, supplierModel, vendorModel, uiContext } = useAppContextNonNull();
    const favorites = useFavorites();

    const [candidates, setCandidates] = useState<IRecentFavoriteCandidate[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [selected, setSelected] = useState<Set<string>>(() => new Set());
    const [isSubmitting, setIsSubmitting] = useState(false);

    const selectAllRef = useRef<HTMLInputElement>(null);

    // Snapshot of the favorites when the list loaded: rows already on the list are hidden, and the
    // snapshot keeps the list stable while the user is ticking boxes.
    const [alreadyFavoritedCount, setAlreadyFavoritedCount] = useState(0);

    useEffect(() => {
        let cancelled = false;

        setIsLoading(true);
        setLoadError(false);

        RecentItemsService.getRecentItemsForFavorites(userModel, supplierModel, vendorModel)
            .then((result) => {
                if (cancelled) {
                    return;
                }

                const notYetFavorited = result.filter((candidate) => !favorites.isFavorite(candidate.catalogItem.guid));

                setAlreadyFavoritedCount(result.length - notYetFavorited.length);
                setCandidates(notYetFavorited);
            })
            .catch((error) => {
                console.error("Failed to load recent items for favorites", error);

                if (!cancelled) {
                    setLoadError(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userModel?.guid, supplierModel?.guid, vendorModel?.guid]);

    const allSelected = candidates.length > 0 && selected.size === candidates.length;
    const someSelected = selected.size > 0 && !allSelected;

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = someSelected;
        }
    }, [someSelected]);

    const toggleOne = (guid: string) => {
        setSelected((prev) => {
            const next = new Set(prev);

            if (next.has(guid)) {
                next.delete(guid);
            } else {
                next.add(guid);
            }

            return next;
        });
    };

    const toggleAll = () => {
        setSelected(allSelected ? new Set() : new Set(candidates.map((candidate) => candidate.catalogItem.guid)));
    };

    const submit = async () => {
        const guids = Array.from(selected);

        if (!guids.length || isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        try {
            const result = await favorites.addFavorites(guids);
            const invalid = new Set(result.invalid || []);
            const addedGuids = guids.filter((guid) => !invalid.has(guid));

            if (invalid.size) {
                uiContext.notifyFn(null, {
                    message: "favorites_some_unavailable",
                    level: NotificationLevelEnum.warning,
                    position: NotificationPositionEnum.bc,
                } as INotification);
            } else {
                uiContext.notifyFn(null, {
                    message: "favorites_added",
                    level: NotificationLevelEnum.success,
                    position: NotificationPositionEnum.bc,
                } as INotification);
            }

            props.onAdded?.(addedGuids);
            props.closeFn();
        } catch (error) {
            uiContext.notifyFn({
                eid: "",
                title: "error",
                message: error instanceof Error ? error.message : "favorite_update_failed",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const viewFullCatalog = () => {
        props.closeFn();
        props.onViewFullCatalog?.();
    };

    const selectedCount = selected.size;
    const rows = useMemo(() => candidates, [candidates]);

    const renderBody = () => {
        if (isLoading) {
            return (
                <div className={"d-flex justify-content-center align-items-center py-5"}>
                    <ActivityIndicator active={true} className={"me-2"} />
                    <OwLabel title={"loading"} noBottomMargin={true} />
                </div>
            );
        }

        if (loadError) {
            return (
                <div className={"text-center py-4"}>
                    <OwP content={"recent_items_load_failed"} />
                </div>
            );
        }

        if (!rows.length) {
            return (
                <div className={"text-center py-4"}>
                    <OwP content={"no_recent_items"} />
                    {alreadyFavoritedCount > 0 ? (
                        <div className={"text-muted text-small mb-3"}>
                            {alreadyFavoritedCount} <FormattedMessage id={"already_in_favorites"} />
                        </div>
                    ) : null}
                    {props.onViewFullCatalog ? (
                        <OwButton className={"btn-outline-secondary"} title={"view_full_catalog"} fn={viewFullCatalog} />
                    ) : null}
                </div>
            );
        }

        return (
            <>
                <div className={"recent-items-toolbar"}>
                    {/* Raw input (not OwCheckbox): the indeterminate state needs a ref, which the shared component cannot forward. */}
                    <label className={"pointer mb-0 d-flex align-items-center gap-2"}>
                        <input
                            ref={selectAllRef}
                            type={"checkbox"}
                            className={"form-check-input m-0"}
                            name={"select-all"}
                            checked={allSelected}
                            onChange={toggleAll}
                            data-testid={"select-all-recent-items"}
                        />
                        <FormattedMessage id={"select_all"} />
                    </label>
                    <span className={"text-muted text-small"}>
                        {selectedCount} / {rows.length}
                        {alreadyFavoritedCount > 0 ? (
                            <>
                                {" · "}
                                {alreadyFavoritedCount} <FormattedMessage id={"already_in_favorites"} />
                            </>
                        ) : null}
                    </span>
                </div>
                <div className={"recent-items-list"} role={"list"}>
                    {rows.map(({ catalogItem, lastQty }) => {
                        const isChecked = selected.has(catalogItem.guid);

                        return (
                            <label
                                key={catalogItem.guid}
                                className={"recent-item-row " + (isChecked ? "is-selected" : "")}
                                role={"listitem"}
                                data-testid={"recent-item-row"}>
                                <input
                                    type={"checkbox"}
                                    className={"form-check-input recent-item-check"}
                                    checked={isChecked}
                                    onChange={() => toggleOne(catalogItem.guid)}
                                    aria-label={catalogItem.name}
                                />
                                <div className={"recent-item-thumb"}>
                                    <OwS3ImageViewer
                                        catalogItem={catalogItem}
                                        imageIndex={IImageSizeEnum.THUMB}
                                        isMicroView={true}
                                        includeAllImages={true}
                                        className={"d-flex no-border"}
                                        hideEmptyImage={false}
                                        microViewSize={"5rem"}
                                        imageSeq={0}
                                    />
                                </div>
                                <div className={"recent-item-name"}>
                                    <div>{catalogItem.name}</div>
                                    <div className={"text-muted text-small"}>{catalogItem.sku}</div>
                                </div>
                                <div className={"recent-item-qty text-muted text-small text-end"}>
                                    <FormattedMessage id={"last_ordered_qty"} />: {lastQty}
                                </div>
                            </label>
                        );
                    })}
                </div>
            </>
        );
    };

    return (
        <div className={"EcommerceFavoritesFromRecentModal"} data-testid={"favorites-from-recent-modal"}>
            <div className={"d-flex justify-content-between align-items-start gap-2 mb-2"}>
                <OwTitle title={"build_from_recent_orders"} tag={"h4"} leftIconName={"fas fa-history"} />
                <button
                    type={"button"}
                    className={"btn btn-sm btn-outline-secondary flex-shrink-0"}
                    aria-label={"close"}
                    onClick={props.closeFn}
                    disabled={isSubmitting}>
                    <i className={"fas fa-times"} />
                </button>
            </div>

            {renderBody()}

            <div className={"modal-footer-actions"}>
                <OwButton className={"btn-outline-secondary"} title={"cancel"} fn={props.closeFn} disabled={isSubmitting} />
                {rows.length ? (
                    <OwButton
                        className={"btn-primary"}
                        title={"add_selected_to_favorites"}
                        postTitle={selectedCount ? ` (${selectedCount})` : ""}
                        leftIconClass={"fas fa-heart"}
                        disabled={!selectedCount || isSubmitting}
                        active={isSubmitting}
                        fn={submit}
                        testId={"add-selected-to-favorites"}
                    />
                ) : null}
            </div>
        </div>
    );
};

export default EcommerceFavoritesFromRecentModal;
